import { enabledToolKeys, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import { type Bundle, isManagedNode, isTaskNode, type Task } from '../../../store/types';
import { EMPTY_REFERENCE_INDEX, findReferenceMatches } from '../../prompt-mention/reference-index';

type ToolCatalogEntry = { kind: string; displayName: string; disabledReason?: string };
type OrphanedToolMention = Pick<ToolCatalogEntry, 'kind' | 'displayName'>;

const taskProse = (task: Task): string[] => [
  task.prompt,
  ...task.fields.map(field => field.prompt),
  ...task.artifacts.map(artifact => artifact.spec),
];

const orphanedIn = (prose: string[], bundle: Bundle, catalog: ToolCatalogEntry[]): OrphanedToolMention[] => {
  const enabled = enabledToolKeys(bundle.tools);
  const texts = prose.filter(Boolean);

  return catalog
    .filter(
      tool => tool.displayName.length >= 3 && !tool.disabledReason && !enabled.has(toolKeyForCatalogKind(tool.kind)),
    )
    .filter(tool =>
      texts.some(
        text =>
          // Share the editor's word boundaries so its chips and this warning agree.
          findReferenceMatches(text, { ...EMPTY_REFERENCE_INDEX, tools: [tool.displayName] }).length > 0,
      ),
    )
    .map(tool => ({ kind: tool.kind, displayName: tool.displayName }));
};

const findOrphanedToolMentions = (bundle: Bundle, catalog: ToolCatalogEntry[]): OrphanedToolMention[] =>
  orphanedIn(
    [
      bundle.norms,
      ...bundle.nodes.flatMap(node => {
        if (isManagedNode(node)) return taskProse(node.task);
        return isTaskNode(node) ? taskProse(node) : [];
      }),
    ],
    bundle,
    catalog,
  );

// Narrowed to one task, so its page can warn where the dangling name actually sits.
const findOrphanedToolMentionsInTask = (
  task: Task,
  bundle: Bundle,
  catalog: ToolCatalogEntry[],
): OrphanedToolMention[] => orphanedIn(taskProse(task), bundle, catalog);

export { findOrphanedToolMentions, findOrphanedToolMentionsInTask };
